import { apiClient } from "@/services/apiClient";

/**
 * Operational metrics service for the platform admin dashboard. Backed by the
 * `GET /platform/metrics/*` endpoints, which query the canonical tables
 * directly. Replaces the deprecated `/platform/analytics/*` surface that read
 * from the now-decommissioned `/events` analytics stream.
 */

export type CountDelta = { current: number; previous: number };
export type AmountDelta = {
  currentMinor: number;
  previousMinor: number;
  currency: string;
};

export type PlatformMetricsSummary = {
  newUsers: {
    total: CountDelta;
    consumers: CountDelta;
    accountants: CountDelta;
    notaries: CountDelta;
  };
  requestsCreated: {
    total: CountDelta;
    notarial: CountDelta;
    accountant: CountDelta;
    enterprise: CountDelta;
  };
  invoicesGenerated: {
    count: CountDelta;
  };
  paidCheckouts: {
    count: CountDelta;
    /** GMV — everything charged, including what is passed straight through. */
    totalAmount: AmountDelta;
    /** Fee + commission: what Gestionalo actually earned (spec platform-fee-model S5). */
    platformRevenue: AmountDelta;
    /**
     * Paid catalog checkouts in range with no recorded fee — the revenue
     * figure understates by their margins. Non-zero means the range reaches
     * back before migration 1700000000090.
     */
    checkoutsWithoutRecordedFee: CountDelta;
  };
  activeTenants: CountDelta;
  /** 0..1 — paidCheckouts.count / requestsCreated.total (0 when no requests). */
  conversionRate: { current: number; previous: number };
};

export type InvoicesByTenantRow = {
  tenantId: string;
  tenantName: string;
  requestCount: number;
  completedCount: number;
  totalAmountMinor: number;
  currency: string;
};

export type RequestsByTypeRow = {
  /** `notarial_request.requestType` or `accountant_request.type`. */
  targetType: string;
  source: "notarial" | "accountant";
  count: number;
  completedCount: number;
};

export type MetricsRangeParams = { from: string; to: string };

/** Server enforces the same cap. */
export const MAX_CUSTOM_RANGE_DAYS = 90;

export type RangePreset = "24h" | "7d" | "30d";

export function rangeFromPreset(preset: RangePreset): MetricsRangeParams {
  const to = new Date();
  const days = preset === "24h" ? 1 : preset === "7d" ? 7 : 30;
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export const platformMetricsService = {
  summary: async (range: MetricsRangeParams): Promise<PlatformMetricsSummary> => {
    const { data } = await apiClient.get<PlatformMetricsSummary>(
      "/platform/metrics/summary",
      { params: range },
    );
    return data;
  },
  invoicesByTenant: async (
    range: MetricsRangeParams,
  ): Promise<InvoicesByTenantRow[]> => {
    const { data } = await apiClient.get<{ tenants: InvoicesByTenantRow[] }>(
      "/platform/metrics/invoices-by-tenant",
      { params: range },
    );
    return data.tenants ?? [];
  },
  requestsByType: async (
    range: MetricsRangeParams,
  ): Promise<RequestsByTypeRow[]> => {
    const { data } = await apiClient.get<{ types: RequestsByTypeRow[] }>(
      "/platform/metrics/requests-by-type",
      { params: range },
    );
    return data.types ?? [];
  },
};

/**
 * Format a minor-units amount (centésimos) as a UYU/USD/etc string for display.
 * Falls back to UYU symbol when currency is unrecognised.
 */
export function formatAmountMinor(
  amountMinor: number,
  currency: string,
  locale = "es-UY",
): string {
  const whole = Math.floor(amountMinor / 100);
  const symbol = currency === "UYU" ? "$" : `${currency} `;
  return `${symbol}${whole.toLocaleString(locale)}`;
}
